'use client';

import type { ReactNode } from 'react';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import type { OptimizelyFlags, VariantProps } from '@/main/lib/providers/OptimizelyProvider';

interface AbTestProps {
  children?: ReactNode;
  flagName: OptimizelyFlags;
  variants?: Record<string, ReactNode>;
  defaultVariant?: string;
}

/**
 * A component for A/B testing that conditionally renders content based on Optimizely feature flags.
 *
 * @param children - Default content to render when no variants are provided and the flag is enabled
 * @param flagName - The name of the Optimizely feature flag to check
 * @param variants - Object mapping variant keys to their respective JSX content
 * @param defaultVariant - The variant key to use as fallback (defaults to 'off')
 *
 * @returns JSX element based on the flag state and variant configuration
 *
 * @example
 * ```tsx
 * // Simple on/off test
 * <AbTest flagName="new-header">
 *   <NewHeader />
 * </AbTest>
 *
 * // Multi-variant test
 * <AbTest
 *   flagName="button-color-test"
 *   variants={{
 *     off: <Button color="blue">Click me</Button>,
 *     variant1: <Button color="red">Click me</Button>,
 *     variant2: <Button color="green">Click me</Button>
 *   }}
 *   defaultVariant="variant1"
 * />
 * ```
 */
export const AbTest = ({ children = null, flagName, variants, defaultVariant = 'off' }: AbTestProps): ReactNode => {
  const { userFlags, optimizely } = useOptimizely();

  // If optimizely is not initialized yet, render the default (which could be nothing)
  if (!optimizely) {
    return variants?.[defaultVariant];
  }

  const flag = userFlags[flagName];

  // First check if we should show the component at all
  const showComponent = (flag as VariantProps)?.enabled ?? false;
  if (!showComponent) {
    return variants?.[defaultVariant];
  }

  // Then decide which variant to show if there are variants
  if (variants) {
    const variantKey = (flag as VariantProps)?.variant ?? defaultVariant;
    return <>{variants[variantKey]}</>;
  }

  // This basically acts as a fallback to show the default content
  return <>{children}</>;
};
